import { TServerErrorResponse, Participant, Conversation } from "@/types";
import { http } from "../httpHandler";
import { links } from "@/constants";

export const PARTICIPANTS_GET = "participants-get";
export const PARTICIPANTS_BULK_GET = "participants-bulk-get";

export const getConversationParticipants = async (
  conversationId: string,
): Promise<Participant[]> => {
  try {
    const response = await http.get<Participant[]>(links.participant.index, {
      params: {
        conversationId,
        includeParticipantNames: true,
      },
    });
    return response.data ?? [];
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getBulkConversationParticipants = async (
  conversationIds: string[],
): Promise<Participant[]> => {
  if (!conversationIds.length) return [];
  try {
    const response = await http.get<Participant[]>(links.participant.index, {
      params: {
        conversationIds: conversationIds.join(","),
        includeParticipantNames: true,
      },
    });
    return response.data ?? [];
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * Fetches participants for a page of conversations and attaches them to each
 * conversation, so a page of table data resolves only once BOTH the
 * conversations and their participants are loaded. This keeps InfinityTable
 * from rendering rows whose Advisor(s)/Client(s) cells are empty and then pop
 * in a moment later (the visible "flash" on initial load and pagination).
 *
 * Participant fetching is auxiliary: if the bulk call fails we still return the
 * conversations so the list renders rather than hiding everything.
 */
export const attachParticipantsToConversations = async (
  conversations: Conversation[],
): Promise<Conversation[]> => {
  const ids = conversations
    .map((c) => c.id)
    .filter((id): id is string => Boolean(id));
  if (!ids.length) return conversations;

  let participants: Participant[];
  try {
    participants = await getBulkConversationParticipants(ids);
  } catch {
    return conversations;
  }

  const byConversationId: Record<string, Participant[]> = {};
  for (const participant of participants) {
    if (!participant.conversationId) continue;
    (byConversationId[participant.conversationId] ??= []).push(participant);
  }

  return conversations.map((conversation) => ({
    ...conversation,
    participants: byConversationId[conversation.id] ?? [],
  }));
};
